import {
  axisPos,
  CELL_PADDING,
  fontFamily,
  fontPx,
  INDENT_STEP,
  type CellRange,
  type LayoutCell,
  type SheetLayout,
  type TextLayout,
} from './layout';
import type { BorderEdge, BorderStyle, FontStyle } from './parser';
import type { SelectionView } from './selection';
import { safeColor } from './parser/colors';

type Style = Record<string, string | number>;

export interface PaneArea {
  layout: SheetLayout;
  zoom: number;
  rowFrom: number;
  rowTo: number;
  colFrom: number;
  colTo: number;
  imageUrls: string[];
}

const BORDER_CSS: Record<BorderStyle, [number, string]> = {
  thin: [1, 'solid'],
  hair: [1, 'dotted'],
  dotted: [1, 'dotted'],
  dashed: [1, 'dashed'],
  dashDot: [1, 'dashed'],
  dashDotDot: [1, 'dashed'],
  medium: [2, 'solid'],
  mediumDashed: [2, 'dashed'],
  mediumDashDot: [2, 'dashed'],
  mediumDashDotDot: [2, 'dashed'],
  slantDashDot: [2, 'dashed'],
  thick: [3, 'solid'],
  double: [3, 'double'],
};

const JUSTIFY: Record<TextLayout['valign'], string> = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };
const ALIGN_ITEMS: Record<TextLayout['align'], string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
  justify: 'stretch',
  distributed: 'stretch',
};

const px = (value: number) => `${value}px`;

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(text: string): string {
  return escapeHtml(text).replace(/"/g, '&quot;');
}

export function css(style: Style): string {
  return escapeAttr(
    Object.entries(style)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => `${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}:${value}`)
      .join(';'),
  );
}

function div(className: string, style: Style, content = ''): string {
  return `<div class="${className}" style="${css(style)}">${content}</div>`;
}

function fontStyle(font: FontStyle, zoom: number, scale: number, color?: string): Style {
  const decorations = [font.underline ? 'underline' : '', font.strike ? 'line-through' : ''].filter(Boolean);
  const style: Style = {
    fontFamily: fontFamily(font.fontName),
    fontSize: px(fontPx(font) * zoom * scale),
    fontWeight: font.bold ? '700' : '400',
    fontStyle: font.italic ? 'italic' : 'normal',
    textDecoration: decorations.length
      ? `${decorations.join(' ')}${font.underline === 'double' ? ' double' : ''}`
      : 'none',
  };
  const textColor = safeColor(color ?? font.color);
  if (textColor) {
    style.color = textColor;
  }
  if (font.vertAlign) {
    style.verticalAlign = font.vertAlign === 'superscript' ? 'super' : 'sub';
  }
  return style;
}

function rotationStyle(rotation: number): Style {
  if (rotation === 255) {
    return { writingMode: 'vertical-lr', textOrientation: 'upright' };
  }
  if (rotation === 90) {
    return { writingMode: 'vertical-rl', transform: 'rotate(180deg)' };
  }
  if (rotation === 180) {
    return { writingMode: 'vertical-rl' };
  }
  const angle = rotation > 90 ? rotation - 90 : -rotation;
  return { display: 'inline-block', transform: `rotate(${angle}deg)`, whiteSpace: 'pre' };
}

function edgeCss(edge: BorderEdge): [number, string] {
  const [width, style] = BORDER_CSS[edge.style];
  return [width, `${width}px ${style} ${safeColor(edge.color) ?? '#000000'}`];
}

function collectItems(area: PaneArea): LayoutCell[] {
  const { layout, rowFrom, rowTo, colFrom, colTo } = area;
  const result: LayoutCell[] = [];
  for (let row = rowFrom; row < rowTo; row++) {
    const rowItems = layout.rowItems[row];
    if (!rowItems) {
      continue;
    }
    for (const item of rowItems) {
      if (item.maxCol >= colFrom && item.minCol < colTo) {
        result.push(item);
      }
    }
  }
  layout.mergeItems.forEach((item) => {
    if (item.rowEnd >= rowFrom && item.row < rowTo && item.maxCol >= colFrom && item.minCol < colTo) {
      result.push(item);
    }
  });
  return result;
}

export function renderPane(area: PaneArea): string {
  const { layout, zoom, rowFrom, rowTo, colFrom, colTo } = area;
  const X = (index: number) => Math.round(axisPos(layout.columns, index) * zoom);
  const Y = (index: number) => Math.round(axisPos(layout.rows, index) * zoom);
  const items = collectItems(area);
  const grid: string[] = [];
  const erasers: string[] = [];
  const fills: string[] = [];
  const bars: string[] = [];
  const icons: string[] = [];
  const texts: string[] = [];
  const edges: string[] = [];
  const diagonals: string[] = [];

  if (layout.sheet.showGridLines) {
    const top = Y(rowFrom);
    const bottom = Y(rowTo);
    const left = X(colFrom);
    const right = X(colTo);
    let previous = -1;
    for (let col = colFrom; col <= colTo; col++) {
      const x = X(col);
      if (x === previous) {
        continue;
      }
      previous = x;
      grid.push(div('xv-pane__grid', { left: px(x), top: px(top), width: '1px', height: px(bottom - top) }));
    }
    previous = -1;
    for (let row = rowFrom; row <= rowTo; row++) {
      const y = Y(row);
      if (y === previous) {
        continue;
      }
      previous = y;
      grid.push(div('xv-pane__grid', { left: px(left), top: px(y), width: px(right - left), height: '1px' }));
    }
  }

  items.forEach((item) => {
    const x1 = X(item.col);
    const x2 = X(item.colEnd + 1);
    const y1 = Y(item.row);
    const y2 = Y(item.rowEnd + 1);
    const text = item.text;

    if (item.erase) {
      erasers.push(
        div('xv-pane__eraser', { left: px(x1 + 1), top: px(y1 + 1), width: px(x2 - x1 - 1), height: px(y2 - y1 - 1) }),
      );
    }

    if (text?.mode === 'line' && (text.clipFrom < item.col || text.clipTo > item.colEnd)) {
      const left = X(text.clipFrom);
      erasers.push(
        div('xv-pane__eraser', {
          left: px(left + 1),
          top: px(y1 + 1),
          width: px(X(text.clipTo + 1) - left - 1),
          height: px(y2 - y1 - 1),
        }),
      );
    }
    const background = safeColor(item.background);
    if (background) {
      fills.push(
        div('xv-pane__fill', {
          left: px(x1),
          top: px(y1),
          width: px(x2 - x1 + 1),
          height: px(y2 - y1 + 1),
          background,
        }),
      );
    }

    if (item.dataBar && safeColor(item.dataBar.color)) {
      const { color, ratio, gradient } = item.dataBar;
      bars.push(
        div('xv-pane__bar', {
          left: px(x1 + 2),
          top: px(y1 + 2),
          width: px(Math.max((x2 - x1 - 4) * ratio, 0)),
          height: px(Math.max(y2 - y1 - 3, 0)),
          background: gradient ? `linear-gradient(to right, ${color}, ${color}22)` : color,
          border: `1px solid ${color}`,
        }),
      );
    }
    if (item.icon) {
      const height = y2 - y1 - 1;
      icons.push(
        div(
          'xv-pane__icon',
          {
            left: px(x1 + 2),
            top: px(y1 + 1),
            height: px(height),
            lineHeight: px(height),
            fontSize: px(14 * zoom),
            color: item.icon.color,
          },
          escapeHtml(item.icon.symbol),
        ),
      );
    }
    if (text) {
      texts.push(renderText(item, text, X, Y, zoom));
    }

    if (item.borders) {
      const { top, right, bottom, left, diagonalUp, diagonalDown } = item.borders;
      const horizontal = (edge: BorderEdge, y: number) => {
        const [width, border] = edgeCss(edge);
        const shift = Math.floor((width - 1) / 2);
        edges.push(
          div('xv-pane__edge', {
            left: px(x1 - shift),
            top: px(y - shift),
            width: px(x2 - x1 + width),
            height: '0',
            borderTop: border,
          }),
        );
      };
      const vertical = (edge: BorderEdge, x: number) => {
        const [width, border] = edgeCss(edge);
        const shift = Math.floor((width - 1) / 2);
        edges.push(
          div('xv-pane__edge', {
            left: px(x - shift),
            top: px(y1 - shift),
            width: '0',
            height: px(y2 - y1 + width),
            borderLeft: border,
          }),
        );
      };
      if (top) {
        horizontal(top, y1);
      }
      if (bottom) {
        horizontal(bottom, y2);
      }
      if (left) {
        vertical(left, x1);
      }
      if (right) {
        vertical(right, x2);
      }

      if (diagonalUp || diagonalDown) {
        const width = x2 - x1;
        const height = y2 - y1;
        const line = (edge: BorderEdge, from: number, to: number) =>
          `<line x1="0" y1="${from}" x2="${width}" y2="${to}" stroke="${escapeAttr(
            edge.color ?? '#000000',
          )}" stroke-width="${BORDER_CSS[edge.style][0]}" />`;
        diagonals.push(
          `<svg class="xv-pane__diagonal" style="${css({
            left: px(x1),
            top: px(y1),
            width: px(width),
            height: px(height),
          })}">${diagonalDown ? line(diagonalDown, 0, height) : ''}${
            diagonalUp ? line(diagonalUp, height, 0) : ''
          }</svg>`,
        );
      }
    }
  });

  const images = layout.sheet.images.map((image, index) => {
    const left = X(image.from.col) + image.from.colOffset * zoom;
    const top = Y(image.from.row) + image.from.rowOffset * zoom;
    const right = image.to ? X(image.to.col) + image.to.colOffset * zoom : left + (image.width ?? 0) * zoom;
    const bottom = image.to ? Y(image.to.row) + image.to.rowOffset * zoom : top + (image.height ?? 0) * zoom;
    const style = css({
      left: px(left),
      top: px(top),
      width: px(Math.max(right - left, 0)),
      height: px(Math.max(bottom - top, 0)),
    });
    return `<img class="xv-pane__image" src="${escapeAttr(area.imageUrls[index] ?? '')}" style="${style}" alt="" />`;
  });

  return [grid, erasers, fills, bars, icons, texts, edges, diagonals, images].map((layer) => layer.join('')).join('');
}

function renderText(
  item: LayoutCell,
  text: TextLayout,
  X: (index: number) => number,
  Y: (index: number) => number,
  zoom: number,
): string {
  const font = fontStyle(text.font, zoom, text.fontScale, text.color);
  const lineHeight = px(text.lineHeight * zoom * text.fontScale);
  const content = text.runs
    ? text.runs
        .map(
          (run) =>
            `<span style="${css(fontStyle(run.font, zoom, text.fontScale, run.font.color ?? text.color))}">${escapeHtml(
              run.text,
            )}</span>`,
        )
        .join('')
    : escapeHtml(text.content);
  const top = Y(item.row) + 1;
  const height = Y(item.rowEnd + 1) - Y(item.row) - 1;

  if (text.mode === 'line') {
    const left = X(text.clipFrom) + 1;
    const ownHeight = text.lineHeight * zoom;
    const offsetTop =
      text.valign === 'top' ? 0 : text.valign === 'middle' ? (height - ownHeight) / 2 : height - ownHeight;
    return div(
      'xv-pane__text xv-pane__text--line',
      { left: px(left), top: px(top), width: px(X(text.clipTo + 1) - left), height: px(height) },
      div('xv-pane__content', { ...font, left: px(text.offset * zoom - 1), top: px(offsetTop), lineHeight }, content),
    );
  }

  const left = X(item.col) + 1;
  const padding = CELL_PADDING * zoom;
  const indent = text.indent * INDENT_STEP * zoom;
  const box: Style = {
    left: px(left),
    top: px(top),
    width: px(X(item.colEnd + 1) - left),
    height: px(height),
    paddingLeft: px(padding + (text.align === 'right' ? 0 : indent)),
    paddingRight: px(padding + (text.align === 'right' ? indent : 0)),
    justifyContent: JUSTIFY[text.valign],
  };

  if (text.mode === 'split') {
    const part = (value: string) => `<span style="${css(font)}">${escapeHtml(value)}</span>`;
    return div(
      'xv-pane__text xv-pane__text--split',
      { ...box, alignItems: JUSTIFY[text.valign], lineHeight, ...font },
      `${part(text.fill.before)}<span class="xv-pane__spacer"></span>${part(text.fill.after)}`,
    );
  }

  const inner: Style = {
    ...font,
    lineHeight,
    textAlign: text.align === 'distributed' ? 'justify' : text.align,
    whiteSpace: text.wrap ? 'pre-wrap' : 'pre',
  };
  if (text.align === 'distributed') {
    inner.textAlignLast = 'justify';
  }
  if (text.rotation) {
    Object.assign(inner, rotationStyle(text.rotation));
    box.alignItems = [90, 180, 255].includes(text.rotation) ? ALIGN_ITEMS[text.align] : 'center';
  }
  return div('xv-pane__text xv-pane__text--block', box, div('xv-pane__content', inner, content));
}

export function renderSelection(layout: SheetLayout, zoom: number, selection: SelectionView | null): string {
  if (!selection) {
    return '';
  }
  const X = (index: number) => Math.round(axisPos(layout.columns, index) * zoom);
  const Y = (index: number) => Math.round(axisPos(layout.rows, index) * zoom);
  const frame = (range: CellRange) => ({
    left: X(range.colFrom),
    top: Y(range.rowFrom),
    right: X(range.colTo + 1),
    bottom: Y(range.rowTo + 1),
  });
  const outline = (range: CellRange): Style => {
    const { left, top, right, bottom } = frame(range);
    return { left: px(left - 1), top: px(top - 1), width: px(right - left + 3), height: px(bottom - top + 3) };
  };

  const range = frame(selection.range);
  const active = frame(selection.active);
  const isRange =
    range.left !== active.left ||
    range.top !== active.top ||
    range.right !== active.right ||
    range.bottom !== active.bottom;
  let html = '';
  if (isRange) {
    const width = range.right - range.left;
    const height = range.bottom - range.top;
    const hole = {
      left: active.left - range.left,
      top: active.top - range.top,
      right: active.right - range.left,
      bottom: active.bottom - range.top,
    };
    const path = `M0 0H${width}V${height}H0Z M${hole.left} ${hole.top}H${hole.right}V${hole.bottom}H${hole.left}Z`;
    html += `<svg class="xv-pane__shade" style="${css({
      left: px(range.left),
      top: px(range.top),
      width: px(width),
      height: px(height),
    })}"><path d="${path}" fill-rule="evenodd" /></svg>`;
  }
  html += div('xv-pane__range', outline(selection.range));
  html += div('xv-pane__handle', { left: px(range.right - 3), top: px(range.bottom - 3) });
  if (selection.copied) {
    html += div('xv-pane__copied', outline(selection.copied));
  }
  return html;
}
